import { HttpClient } from '@angular/common/http';
import { inject, Injectable, Signal, signal } from '@angular/core';
import { Invoice, InvoiceDetails, InvoiceList, MovementRequest, MovementResponse, Obligation } from '@core/models';
import { environment } from '@env/environment';
import { UserStatus } from '@shared/enums';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { PaginationDataService } from './pagination-data.service';

@Injectable({
  providedIn: 'root',
})
export class InvoiceApiService {
  private _apiUrl = `${environment.baseUrl}/invoices`;
  private _invoicesData = inject(PaginationDataService<InvoiceList>);
  private _statusFilter = signal<number[]>([UserStatus.ACTIVE]);

  constructor(private http: HttpClient) {
    this._invoicesData.setFilterOptions({
      filterFunctionSearch: (data, query) => this.filterFunctionSearch(data, query),
      applyAdditionalFilters: (items) => this.applyAdditionalFilters(items),
    });
  }

  /** Operaciones */

  fetchInvoices(year: number, month: number): Observable<InvoiceList[]> {
    return this.http.get<InvoiceList[]>(`${this._apiUrl}/${year}/${month}`).pipe(
      tap((data) => this._invoicesData.setItems(data)),
      catchError((err) => throwError(() => err.error?.message)),
    );
  }

  processInvoice(invoiceId: number): Observable<Invoice> {
    const form = new FormData();
    form.append('invoiceId', invoiceId.toString());

    return this.http.post<Invoice>(`${this._apiUrl}/process`, form).pipe(
      tap((data) => this.updateInvoice(data)),
      catchError((err) => throwError(() => err.error.message)),
    );
  }

  cancelInvoice(invoiceId: number): Observable<Invoice> {
    const form = new FormData();
    form.append('invoiceId', invoiceId.toString());

    return this.http.post<Invoice>(`${this._apiUrl}/cancel`, form).pipe(
      tap((data) => this.updateInvoice(data)),
      catchError((err) => throwError(() => err.error.message)),
    );
  }

  // LLevar a otro service de movimientos
  createMovement(movement: MovementRequest): Observable<MovementResponse> {
    return this.http.post<MovementResponse>(`${environment.baseUrl}/movements`, movement).pipe(
      catchError((err) => throwError(() => err.error.message)),
    );
  }

  private updateInvoice(item: Invoice) {
    const items = this._invoicesData.getItems()()
      .map((i) => (i.id === item.id ? {...i, statusId: item.statusId} : i));
    this._invoicesData.setItems(items);
  }

  // Getters - Estos metodos pueden ser genericos como se realizo en la primera
  // implementación para no estar repitiendolos y seguir manejando la composición
  get invoices(): Signal<InvoiceList[]> {
    return this._invoicesData.getItemsWithFilter();
  }

  get totalRecords(): Signal<number> {
    return this._invoicesData.getTotalRecords();
  }

  get recordsPerPage(): Signal<number> {
    return this._invoicesData.getRecordsPerPage() as any;
  }

  get sortField(): Signal<keyof InvoiceList | null> {
    return this._invoicesData.getSortField();
  }

  get sortDirection(): Signal<'asc' | 'desc'> {
    return this._invoicesData.getSortDirection();
  }

  toggleSort(field: keyof InvoiceList) {
    this._invoicesData.toggleSort(field);
  }

  /** Cambiar la página actual */
  setCurrentPage(page: number): void {
    this._invoicesData.setCurrentPage(page);
  }

  /** Cambiar la cantidad de registros por página */
  setRecordsPerPage(records: number): void {
    this._invoicesData.setRecordsPerPage(records);
  }

  /** Define los filtros */
  get statusFilter(): Signal<number[] | null> {
    return this._statusFilter;
  }

  setStatusFilter(statusId: number[]): void {
    this._statusFilter.set(statusId);
  }

  /** FILTROS */

  private filterFunctionSearch(data: InvoiceList, query: string): boolean {
    // return data.obligationNumber.toString().includes(query) || data.associateId.toLowerCase().includes(query);
    return true;
  }

  private applyAdditionalFilters(items: InvoiceList[]): InvoiceList[] {
    let filtered = [...items];

    // const filterValue = this.statusFilter();
    // if (filterValue && filterValue.length) {
    //   filtered = filtered.filter((user) => filterValue.includes(user.statusId));
    // }

    return filtered;
  }
}
